import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  ManageMyRepairResponse,
  ProductTypeQuestion,
} from '../../../../types/manage-my-repair-types';

import {
  GAS_EMERGENCY_CONTACTS,
  GAS_EMERGENCY_REASONS,
  GAS_SAFETY_ANSWERS,
  GasSafetyAnswer,
  MOBILE_DEVICE_REGEX,
} from './product-type-question.constants';

import posthog from 'posthog-js';

export interface ProductTypeQuestionState {
  shouldAskProductTypeQuestion: boolean;
  selectedOption: string | null;
  shouldDisplayGasQuestion: boolean;
  gasSafetyAnswer: GasSafetyAnswer | null;
  gasEmergencyReason: string | null;
  showGasEmergencyContent: boolean;
  confirmNationalGasContact?: boolean;
}

export interface ProductTypeSelectionChange {
  option: string | null;
  resetClaimSelection: boolean;
}

@Component({
  selector: 'claims-repair-product-type-question',
  templateUrl: './product-type-question.component.html',
  styleUrls: ['./product-type-question.component.scss'],
})
export class ProductTypeQuestionComponent implements OnChanges, OnInit {
  @Input() claim: ManageMyRepairResponse | null = null;
  @Input() gasSafetyAnswers: GasSafetyAnswer[] = [...GAS_SAFETY_ANSWERS];
  @Input() claimType: 'REPAIR' | 'SERVICE';
  @Input() resetStateToggle = false;
  @Output() stateChange = new EventEmitter<ProductTypeQuestionState>();
  @Output() selectionChange = new EventEmitter<ProductTypeSelectionChange>();
  @Output() gasSafetyAnswerSelected = new EventEmitter<GasSafetyAnswer>();

  private productTypeQuestion: ProductTypeQuestion | undefined;
  options: string[] = [];
  selectedOption: string | null = null;
  gasSafetyAnswer: GasSafetyAnswer | null = null;
  gasEmergencyReason: string | null = null;
  shouldAskProductTypeQuestion = false;
  hasGasOption = false;
  private isGasPlan = false;
  private isTV = false;
  private isCombinationAppliance = false;
  readonly gasEmergencyReasons = GAS_EMERGENCY_REASONS;
  readonly gasEmergencyContacts = GAS_EMERGENCY_CONTACTS;

  isCallLinkInteractive = false;
  confirmNationalGasContact = false;

  ngOnInit(): void {
    this.updateCallLinkInteractivity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.claim) {
      this.setupProductTypeQuestionFromClaim();
    }

    if (
      changes.resetStateToggle &&
      !changes.resetStateToggle.firstChange &&
      changes.resetStateToggle.currentValue !==
        changes.resetStateToggle.previousValue
    ) {
      this.resetState();
    }
  }

  @HostListener('window:resize')
  handleResize(): void {
    this.updateCallLinkInteractivity();
  }

  get shouldShowGasSafetyQuestion(): boolean {
    if (this.claimType === 'SERVICE') {
      return true;
    }

    if (this.isGasPlan && !this.shouldAskProductTypeQuestion) {
      return true;
    }

    return (
      this.hasGasOption &&
      !!this.selectedOption &&
      this.containsGas(this.selectedOption)
    );
  }

  get showGasEmergencyContent(): boolean {
    return (
      this.shouldShowGasSafetyQuestion &&
      this.gasSafetyAnswer === 'YES' &&
      !!this.gasEmergencyReason
    );
  }

  get productTypeQuestionTitle(): string {
    if (this.isTV) return 'What size is your TV screen?';
    return this.isCombinationAppliance
      ? 'First, which product would you like repaired?'
      : 'First, what type of product do you have?';
  }

  handleSelection(option: string): void {
    if (!option || option === this.selectedOption) {
      return;
    }

    this.selectedOption = option;
    this.gasSafetyAnswer = null;
    this.gasEmergencyReason = null;

    this.selectionChange.emit({
      option: option,
      resetClaimSelection: this.shouldAskProductTypeQuestion,
    });

    this.emitState();
  }

  handleGasSafetySelection(answer: GasSafetyAnswer): void {
    if (this.gasSafetyAnswer === answer) {
      return;
    }

    this.gasSafetyAnswer = answer;
    if (answer !== 'YES') {
      this.gasEmergencyReason = null;
      this.confirmNationalGasContact = false;
    }

    posthog.capture('mmr-gas-safety-question', {
      question: "Do you smell gas or think there's a carbon monoxide leak?",
      answer: answer,
    });

    this.gasSafetyAnswerSelected.emit(answer);
    this.emitState();
  }

  handleGasEmergencyReasonSelection(reason: string): void {
    if (this.gasEmergencyReason === reason) {
      return;
    }

    posthog.capture('mmr-gas-safety-question', {
      question: 'What are you reporting?',
      answer: reason,
    });

    this.gasEmergencyReason = reason;
    this.emitState();
  }

  handleCallLinkClick(event: Event): void {
    if (!this.isCallLinkInteractive) {
      event.preventDefault();
    }
  }

  private setupProductTypeQuestionFromClaim(): void {
    const previousSelectedOption = this.selectedOption;
    const previousGasSafetyAnswer = this.gasSafetyAnswer;
    const previousGasEmergencyReason = this.gasEmergencyReason;

    this.clearState();

    const claim = this.claim;
    this.isGasPlan = !!claim?.isGas;
    this.isTV = !!claim?.isTV;
    this.productTypeQuestion = claim?.productTypeQuestion;
    this.isCombinationAppliance =
      !!this.productTypeQuestion?.isCombinationAppliance;

    if (!this.productTypeQuestion) {
      this.emitState();
      return;
    }

    const options = (this.productTypeQuestion.options ?? [])
      .map((value) => value?.trim())
      .filter((value): value is string => !!value);
    if (this.isTV) {
      this.sortScreenSizes(options);
    }

    this.options = options;
    this.hasGasOption = options.some((value) => this.containsGas(value));
    this.shouldAskProductTypeQuestion =
      !!this.productTypeQuestion && options.length > 0;

    if (!this.shouldAskProductTypeQuestion) {
      this.emitState();
      return;
    }

    if (!!previousSelectedOption && options.includes(previousSelectedOption)) {
      this.selectedOption = previousSelectedOption;

      if (previousGasSafetyAnswer) {
        this.gasSafetyAnswer = previousGasSafetyAnswer;

        const reasonStillValid = this.gasEmergencyReasons.some(
          (reason) => reason.value === previousGasEmergencyReason
        );

        this.gasEmergencyReason =
          previousGasSafetyAnswer === 'YES' && reasonStillValid
            ? previousGasEmergencyReason
            : null;
      }
    }

    this.emitState();
  }

  resetState(): void {
    if (!this.productTypeQuestion || this.options.length === 0) {
      this.clearState();
      this.emitState();
      return;
    }

    const hadSelection =
      this.selectedOption !== null ||
      this.gasSafetyAnswer !== null ||
      this.gasEmergencyReason !== null;

    this.selectedOption = null;
    this.gasSafetyAnswer = null;
    this.gasEmergencyReason = null;
    this.confirmNationalGasContact = false;

    if (hadSelection) {
      this.selectionChange.emit({
        option: this.selectedOption,
        resetClaimSelection: true,
      });
    }

    this.emitState();
  }

  handleConfirmationOfContactToNationalGas(): void {
    this.confirmNationalGasContact = !this.confirmNationalGasContact;
    this.emitState();
  }

  private clearState(): void {
    this.options = [];
    this.selectedOption = null;
    this.gasSafetyAnswer = null;
    this.gasEmergencyReason = null;
    this.confirmNationalGasContact = false;
    this.shouldAskProductTypeQuestion = false;
    this.hasGasOption = false;
    this.isCombinationAppliance = false;
  }

  private containsGas(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }

    const normalized = value.trim().toLowerCase();
    return normalized.includes('gas');
  }

  private emitState(): void {
    const shouldDisplayGasQuestion = this.shouldShowGasSafetyQuestion;
    const showGasEmergencyContent =
      shouldDisplayGasQuestion &&
      this.gasSafetyAnswer === 'YES' &&
      !!this.gasEmergencyReason;

    this.stateChange.emit({
      shouldAskProductTypeQuestion: this.shouldAskProductTypeQuestion,
      selectedOption: this.selectedOption,
      shouldDisplayGasQuestion,
      gasSafetyAnswer: this.gasSafetyAnswer,
      gasEmergencyReason: this.gasEmergencyReason,
      showGasEmergencyContent,
      confirmNationalGasContact: showGasEmergencyContent
        ? this.confirmNationalGasContact
        : false,
    });
  }

  private updateCallLinkInteractivity(): void {
    this.isCallLinkInteractive = this.detectMobileDevice();
  }

  private detectMobileDevice(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const hasTouchPoints = Number(navigator.maxTouchPoints || 0) > 0;
    return hasTouchPoints || MOBILE_DEVICE_REGEX.test(navigator.userAgent);
  }

  formatOption(option: string): string {
    if (this.isTV) {
      const formattedOption = option.replace(/&/g, 'and');
      return formattedOption
        .replace(/^\s*Television\s-\s|\(\w*\)\s*/g, '')
        .trim();
    }
    return option;
  }

  sortScreenSizes(arr: string[]): string[] {
    return arr.sort((a, b) => {
      const numA = parseInt(a.match(/(\d+)\s*"/)?.[1] ?? '0', 10);
      const numB = parseInt(b.match(/(\d+)\s*"/)?.[1] ?? '0', 10);
      return numA - numB;
    });
  }
}
